import React from 'react'
import cx from 'classnames'

import Cell from './Cell/Cell'
import type { GridCellProps } from './Cell/Cell'

import s from './Grid.scss'


export const aligns = [ 'center', 'start', 'end', 'baseline', 'stretch' ] as const
export const justifies = [ 'between', 'center', 'start', 'end' ] as const
export const columnsSizes = [ 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12 ] as const
export const gutterSizes = [ 4, 8, 12, 16, 24, 32, 48 ] as const

export type Align = typeof aligns[number]
export type Justify = typeof justifies[number]
export type ColumnsSize = typeof columnsSizes[number]
export type GutterSize = typeof gutterSizes[number]

export type GridProps = {
  className?: string
  columns: ColumnsSize
  gutter?: GutterSize
  align?: Align
  justify?: Justify
  role?: string
  noWrap?: boolean
  dataTestId?: string
  ref?: React.MutableRefObject<HTMLDivElement>
  children: React.ReactChildren | JSX.Element[]
}

type GridComponent = React.FC<GridProps> & {
  Cell: React.FC<GridCellProps>
}

const Grid: GridComponent = (props) => {
  const {
    children, className, columns = 1, gutter,
    align = 'start', justify = 'start', role = 'list', noWrap,
    dataTestId, ref,
  } = props

  const rootClassName = cx(className, 'flex', {
    [`align-${align}`]: align,
    [`justify-${align}`]: justify,
    'flex-wrap': !noWrap,
    [s[`gutter-${gutter}`]]: gutter,
  })

  return (
    <div
      className={rootClassName}
      role={role}
      data-testid={dataTestId}
      ref={ref}
    >
      {
        React.Children.map<any, React.ReactNode>(children, (child) => {
          if (child) {
            const cellProps = { columns, gutter } as { columns: ColumnsSize, gutter: GutterSize }

            if (React.isValidElement(child) && child.type === Cell) {
              return React.cloneElement(child, cellProps)
            }

            return (
              <Grid.Cell {...cellProps}>
                {child}
              </Grid.Cell>
            )
          }
        })
      }
    </div>
  )
}

Grid.Cell = Cell


export default React.memo(Grid)
